import React from 'react';
import { Globe } from 'lucide-react';

const AboutSection: React.FC = () => {
  return (
    <section className="bg-paper py-24 lg:py-32">
      <div className="max-w-editorial mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">

          {/* Left Content */}
          <div className="md:col-span-7">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-6">
              — The story
            </p>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 mb-10 leading-[1.1] tracking-tight">
              The island standard.
            </h2>
            <div className="space-y-6 max-w-prose">
              <p className="text-ink-700 text-lg leading-[1.65]">
                In the Caribbean, a machete is more than just hardware — it is an extension of the hand.
                It defines everyday island life: clearing a path to the beach, manicuring the villa garden,
                or cracking open a fresh green coconut.
              </p>
              <p className="text-ink-700 text-lg leading-[1.65]">
                <span className="text-ink-950 font-medium">The Machete Man</span> was founded to bring this
                culture of utility to the North. As Incolma's exclusive Canadian distributor and
                wholesaler, every authentic Incolma blade sold here comes through us — directly from
                their Colombian forge, with the same soul and durability as the ones used daily in
                the tropics.
              </p>
              <p className="text-ink-700 text-lg leading-[1.65]">
                Today, from our central hub in the{' '}
                <span className="text-ink-950 font-medium">Greater Toronto Area</span>, we bring blades
                to your door that offer a slice of the island lifestyle — rugged, reliable,
                and effortlessly versatile.
              </p>
            </div>

            {/* Our Promise */}
            <div className="mt-16 pt-12 border-t border-ink-200">
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-8">
                — Our promise
              </p>
              <div className="space-y-8">
                {[
                  {
                    n: '01',
                    title: 'Authentic, always',
                    body: 'Every blade is factory-direct Incolma from Manizales — no grey-market stock, no lookalikes. If it carries our name, it carries theirs.',
                  },
                  {
                    n: '02',
                    title: 'Fair to every partner',
                    body: 'One honest price list, retail or trade. The corner garden centre gets the same terms as the big chain — 12 units opens a wholesale account, no carton commitments.',
                  },
                  {
                    n: '03',
                    title: 'Backed after the sale',
                    body: 'A sale without service is nothing. A real person answers on WhatsApp — before you buy, and for every sharpening question, care tip, or warranty claim after.',
                  },
                ].map((item) => (
                  <div key={item.n} className="group flex gap-6 border-l-2 border-ink-200 hover:border-ember pl-6 transition-colors duration-300">
                    <span className="font-mono text-[11px] text-ember pt-1.5 flex-shrink-0">{item.n}</span>
                    <div>
                      <h3 className="font-editorial italic text-2xl text-ink-950 tracking-tight group-hover:text-ember transition-colors duration-300 ease-editorial">
                        {item.title}.
                      </h3>
                      <p className="text-sm text-ink-700 leading-relaxed mt-2 max-w-prose">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Image + Pipeline */}
          <div className="md:col-span-5">
            {/* Image */}
            <div className="aspect-[4/3] mb-8 overflow-hidden border border-ink-200 group relative">
              <img
                src="/images/colombian-forge.webp"
                alt="Master blacksmith forging a machete blade in a Colombian foundry"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-editorial"
                style={{ objectPosition: 'center' }}
              />
            </div>

            {/* Pipeline card */}
            <div className="bg-paper-bright p-8 lg:p-10 border border-ink-200">
              <div className="w-10 h-10 flex items-center justify-center bg-ember text-paper-warm mb-6">
                <Globe className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="font-editorial italic font-light text-2xl text-ink-950 mb-8 tracking-tight">
                Forge to your door.
              </h3>

              <ul className="space-y-8 relative">
                <div className="absolute left-[5px] top-3 bottom-3 w-px bg-ink-200" />

                <li className="relative pl-8">
                  <div className="absolute left-0 top-2 w-2.5 h-2.5 bg-ink-300 rounded-full ring-4 ring-paper-bright" />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">
                    01 — Origin
                  </p>
                  <h4 className="font-editorial italic text-lg text-ink-950 mb-1">Colombia</h4>
                  <p className="text-sm text-ink-700">
                    Forged for the demands of the coast. Precision heat treatment meets tropical resilience.
                  </p>
                </li>

                <li className="relative pl-8">
                  <div className="absolute left-0 top-2 w-2.5 h-2.5 bg-ink-300 rounded-full ring-4 ring-paper-bright" />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">
                    02 — Transit
                  </p>
                  <h4 className="font-editorial italic text-lg text-ink-950 mb-1">Direct ocean freight</h4>
                  <p className="text-sm text-ink-700">
                    Containerized shipping from Port of Cartagena. Direct from the Caribbean to you.
                  </p>
                </li>

                <li className="relative pl-8">
                  <div className="absolute left-0 top-2 w-2.5 h-2.5 bg-ember rounded-full ring-4 ring-paper-bright" />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1">
                    03 — Hub
                  </p>
                  <h4 className="font-editorial italic text-lg text-ink-950 mb-1">Scarborough warehouse</h4>
                  <p className="text-sm text-ink-700">
                    Quality control inspection, packaging, and dispatch to your door — from our
                    warehouse at 345 Nugget Avenue, with free order pickup by appointment.
                  </p>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;
